import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { rispostaPerQuestaApp } from "@/lib/iscrizione";
import { useParams } from "react-router-dom";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import { UserMessage } from "./UserMessage";
/**
 * Il motore che trasforma il testo in paragrafi, elenchi e tabelle pesa 154 kB
 * e serve SOLO quando arriva una risposta - non nella schermata d'apertura,
 * dove l'installatore sta ancora scrivendo. Si scarica in sottofondo appena la
 * chat e' pronta (vedi il precarico in fondo al file): quando la prima
 * risposta arriva e' gia' li', e nel frattempo non ha rubato secondi al primo
 * avvio, che in cantiere si aspetta con una tacca di segnale.
 */
const MarkdownContent = lazy(() =>
  import("./MarkdownContent").then((m) => ({ default: m.MarkdownContent })),
);
import { SourcesList } from "./SourcesList";
import { SchemaPreview } from "./SchemaPreview";
import { BottoneCopia } from "./BottoneCopia";
import { riconosciSchema, scegliSchema } from "@/lib/schemaImage";
import { titolareSchema } from "@/lib/schemiFonte";
import { AliasTooltip } from "./AliasTooltip";
import { useAliasMatcher } from "@/hooks/useAliasMatcher";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUtenteOpzionale } from "@/hooks/useAuth";
import type { ChatMessage } from "@/lib/types";

/** Le conversazioni vere hanno un id UUID; il triage del banco usa «banco-triage». */
const UUID_CONV = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface Props {
  messages: ChatMessage[];
  loading: boolean;
  onPickSuggestion: (s: string, opts?: { isClarification?: boolean }) => void;
}

const DISLIKE_CATEGORIES = [
  { id: "1", label: "Risposta sbagliata (informazione errata)", tag: "Risposta sbagliata" },
  { id: "2", label: "Mancano informazioni importanti", tag: "Mancano informazioni" },
  { id: "3", label: "Risposta troppo generica", tag: "Troppo generica" },
  { id: "4", label: "Confonde i brand", tag: "Confonde i brand" },
  { id: "5", label: "Cita fonti non pertinenti", tag: "Fonti non pertinenti" },
  { id: "6", label: "Altro", tag: "Altro" },
];

/**
 * Dove finisce «Contatta Datacom».
 *
 * In riunione (Umberto, 27/08) era emerso di NON usare info@, che va ripulita
 * ogni mattina da una montagna di posta: una richiesta d'aiuto ci si perde
 * dentro. Ero andato avanti scrivendo `assistenza@`, che pero' nessuno ha mai
 * confermato che esista - e una casella inventata non e' meglio di una piena:
 * la mail parte, non torna indietro, e nessuno la legge mai.
 *
 * Davide (28/08): «per il momento metterei info@ perche' assistenza non so se
 * esiste, eventualmente lo possiamo cambiare». Quindi info@, che almeno
 * qualcuno la apre. Quando Datacom crea la casella dedicata si cambia questa
 * riga e basta: e' l'unico posto dove l'indirizzo compare.
 */
const EMAIL_ASSISTENZA = "info@datacomtecnologie.it";

function FeedbackToolbar({
  messageId,
  conversationId,
  domanda,
}: {
  messageId: string;
  conversationId: string;
  /** La domanda a cui si riferisce la risposta bocciata: finisce nella mail. */
  domanda?: string;
}) {
  const [rating, setRating] = useState<"like" | "dislike" | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [comment, setComment] = useState("");
  const [rispostaGiusta, setRispostaGiusta] = useState("");

  const user = `datacom-demo-${conversationId}`;
  const disabled = submitting || rating !== null;
  const utente = useUtenteOpzionale();
  const qc = useQueryClient();

  /**
   * Il giudizio resta anche DA NOI (tabella `giudizi`, 24/09/2026), non solo
   * su Dify: serve a contare, per conversazione, quando far comparire
   * l'ultimo salvagente verso i tecnici Datacom. Non blocca il pollice: se
   * la tabella non c'e' ancora o la rete manca, il voto a Dify parte lo stesso.
   */
  const salvaGiudizio = (
    esito: "aiutato" | "non_aiutato",
    extra?: { motivo?: string; commento?: string; risposta_giusta?: string },
  ) => {
    if (!utente || !UUID_CONV.test(conversationId)) return;
    void supabase
      .from("giudizi")
      .insert({
        user_id: utente.id,
        conversation_id: conversationId,
        dify_message_id: messageId,
        esito,
        motivo: extra?.motivo ?? null,
        commento: extra?.commento || null,
        risposta_giusta: extra?.risposta_giusta || null,
      })
      .then(({ error }) => {
        if (!error) void qc.invalidateQueries({ queryKey: ["giudizi", conversationId] });
      });
  };

  const sendLike = async () => {
    if (disabled) return;
    setSubmitting(true);
    const { error } = await supabase.functions.invoke("send-dify-feedback", {
      body: { message_id: messageId, rating: "like", user, content: "" },
    });
    setSubmitting(false);
    if (error) {
      toast.error("Feedback non inviato", {
        description: "Riprova tra qualche istante o riapri la finestra.",
      });
      return;
    }
    setRating("like");
    salvaGiudizio("aiutato");
    toast.success("Grazie per il feedback", {
      description: "Il tuo segnale aiuta a migliorare le risposte.",
    });
  };

  const resetDialog = () => {
    setSelectedCategory("");
    setComment("");
    setRispostaGiusta("");
  };

  const handleDialogChange = (open: boolean) => {
    if (submitting) return;
    setDialogOpen(open);
    if (!open) resetDialog();
  };

  const sendDislike = async () => {
    const cat = DISLIKE_CATEGORIES.find((c) => c.id === selectedCategory);
    if (!cat) return;
    setSubmitting(true);
    const trimmed = comment.trim();
    const giusta = rispostaGiusta.trim();
    // La risposta giusta viaggia nello stesso campo del commento, dietro un
    // separatore fisso: la sentinella sul VPS lo spacca e prepara la bozza
    // della scheda con quel valore gia' al suo posto (da verificare, sempre).
    const content =
      (trimmed ? `[${cat.tag}] ${trimmed}` : `[${cat.tag}]`) +
      (giusta ? ` ‖ RISPOSTA GIUSTA: ${giusta}` : "");
    const { error } = await supabase.functions.invoke("send-dify-feedback", {
      body: { message_id: messageId, rating: "dislike", user, content },
    });
    setSubmitting(false);
    if (error) {
      toast.error("Feedback non inviato", {
        description: "Riprova tra qualche istante o riapri la finestra.",
      });
      return;
    }
    setRating("dislike");
    salvaGiudizio("non_aiutato", { motivo: cat.tag, commento: trimmed, risposta_giusta: giusta });
    setDialogOpen(false);
    resetDialog();
    /**
     * «CONTATTA DATACOM» SOLO QUI, E NON UN PULSANTE SEMPRE VISIBILE.
     *
     * Umberto, 27/08: un pulsante fisso e' un invito a telefonare per tutto, e
     * l'assistenza tecnica di Datacom e' due persone. Ma chi ha appena detto
     * che la risposta non andava bene e' l'unico che ha davvero bisogno di una
     * via d'uscita - e Datacom, vedendo da dove arriva la chiamata, sa anche
     * perche'. La via si apre dove nasce il problema, e si chiude da sola.
     */
    toast.success("Grazie: il tuo segnale migliora le risposte", {
      description: "Se ti serve una mano subito, scrivi a Datacom.",
      duration: 12000,
      action: {
        label: "Contatta Datacom",
        onClick: () => {
          const oggetto = encodeURIComponent("Richiesta assistenza tecnica — da Datacom AI");
          const corpo = encodeURIComponent(
            `Ho fatto questa domanda all'assistente e la risposta non mi ha aiutato.

` +
              `Domanda: ${domanda ?? "—"}

` +
              `Cosa mi serve davvero:
`,
          );
          window.location.href = `mailto:${EMAIL_ASSISTENZA}?subject=${oggetto}&body=${corpo}`;
        },
      },
    });
  };

  return (
    <>
      <div className="flex gap-2">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={sendLike}
                aria-label="Mi ha aiutato"
                className="min-h-11 min-w-11 px-3"
              >
                <ThumbsUp
                  className={rating === "like" ? "text-primary fill-current" : ""}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Mi ha aiutato</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => setDialogOpen(true)}
                aria-label="Non mi ha aiutato"
                className="min-h-11 min-w-11 px-3"
              >
                <ThumbsDown
                  className={rating === "dislike" ? "text-primary fill-current" : ""}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Non mi ha aiutato</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cosa c'è che non va?</DialogTitle>
            <DialogDescription>
              Aiutaci a migliorare le risposte.
            </DialogDescription>
          </DialogHeader>

          <RadioGroup
            value={selectedCategory}
            onValueChange={setSelectedCategory}
            className="gap-2"
          >
            {DISLIKE_CATEGORIES.map((c) => {
              const inputId = `fb-${messageId}-${c.id}`;
              return (
                <div key={c.id} className="flex items-center gap-2">
                  <RadioGroupItem value={c.id} id={inputId} />
                  <Label htmlFor={inputId} className="cursor-pointer font-normal">
                    {c.label}
                  </Label>
                </div>
              );
            })}
          </RadioGroup>

          <div className="space-y-1">
            <Textarea
              placeholder="Aggiungi un commento (opzionale)"
              maxLength={500}
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 500))}
              className="min-h-[80px]"
            />
            <div className="text-xs text-muted-foreground text-right">
              {comment.length}/500
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor={`fb-giusta-${messageId}`} className="text-sm font-normal">
              Se la conosci, qual è la risposta giusta? (facoltativo)
            </Label>
            <Textarea
              id={`fb-giusta-${messageId}`}
              placeholder="Es. la AM-2000N accetta 99 sensori per loop, non 159"
              maxLength={300}
              value={rispostaGiusta}
              onChange={(e) => setRispostaGiusta(e.target.value.slice(0, 300))}
              className="min-h-[60px]"
            />
            <p className="text-xs text-muted-foreground">
              Datacom la verifica sui manuali prima di usarla: non finisce nelle risposte da sola.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleDialogChange(false)}
              disabled={submitting}
            >
              Annulla
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={sendDislike}
              disabled={!selectedCategory || submitting}
            >
              {submitting ? "Invio…" : "Invia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AssistantMessage({
  msg,
  onPickSuggestion,
  conversationId,
  processText,
  domanda,
}: {
  msg: ChatMessage;
  onPickSuggestion: (s: string, opts?: { isClarification?: boolean }) => void;
  conversationId: string;
  processText?: (text: string) => ReactNode;
  domanda?: string;
}) {
  const showFeedback =
    !!msg.messageId && msg.messageId.length > 0 && !msg.fromMock;
  const transparency = msg.transparency ?? null;
  // Il primo schema citato che parli davvero di questa centrale e di questo
  // argomento. Gli altri restano scritti fra le fonti, dove un riferimento
  // sbagliato si legge e si scarta; un disegno sbagliato invece si segue.
  const schemaMostrato = scegliSchema(
    (msg.citations ?? []).map((c) => riconosciSchema(c.document_name ?? c.title)),
    msg.content,
    domanda,
  );
  return (
    <div className="flex w-full justify-start animate-fade-up">
      <div className="min-w-0 max-w-[92%] space-y-2.5 overflow-hidden rounded-2xl rounded-tl-sm border border-foreground/10 bg-foreground/5 px-4 py-3 text-base leading-[1.6] text-card-foreground backdrop-blur-sm sm:max-w-[80%] sm:text-[15px] sm:leading-relaxed">
        <Suspense
          fallback={<div className="whitespace-pre-wrap">{rispostaPerQuestaApp(msg.content)}</div>}
        >
          <MarkdownContent content={rispostaPerQuestaApp(msg.content)} processText={processText} />
        </Suspense>

        {/*
          LO SCHEMA STA NELLA RISPOSTA, NON IN FONDO (Davide, 04/09/2026:
          «se tiro fuori lo schema direttamente nella risposta sarebbe
          meglio»). Prima viveva solo dentro «Fonti consultate», cioe' sotto
          la firma: in cantiere e' il motivo per cui si e' aperta l'app, e
          chiedeva un giro in piu' per arrivarci. Se ne mostra UNO, il primo
          buono: due disegni uno sotto l'altro sono gia' un elenco da leggere.
          Il filtro e' quello che conta — vedi schemaDaMostrare: alla domanda
          sull'AM4000 il recupero allegava la morsettiera dell'AM-8200N.
        */}
        {msg.status !== "clarification" && schemaMostrato && (
          <SchemaPreview schema={schemaMostrato} titolare={titolareSchema(schemaMostrato)} />
        )}

        {/* Fonti consultate (lista completa con badge brand) */}
        {msg.status !== "clarification" && (
          <SourcesList citations={msg.citations} transparency={transparency} />
        )}
        {/* Fallback: solo "Fonte:" se non abbiamo citations strutturate */}
        {msg.source && msg.status !== "clarification" && (!msg.citations || msg.citations.length === 0) && (
          <div className="text-sm text-muted-foreground pt-1 sm:text-[13px]">
            <span className="font-medium text-foreground">Fonte:</span>{" "}
            {msg.source.replace(/\.pdf$/i, "").replace(/_/g, " ").trim()}
          </div>
        )}
        {msg.note && (
          <div className="text-xs italic text-muted-foreground">
            <span className="font-medium not-italic text-foreground/80">Nota:</span> {msg.note}
          </div>
        )}

        {msg.status === "clarification" && msg.suggestions && msg.suggestions.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {msg.suggestions.map((s) => (
              <button
                key={s}
                onClick={() => onPickSuggestion(s, { isClarification: true })}
                className="inline-flex min-h-11 items-center rounded-full border border-border bg-background/50 px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground sm:min-h-9 sm:px-3 sm:py-1.5 sm:text-xs"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {msg.fromMock && (
          <div className="pt-1">
            <Badge variant="outline" className="text-[10px]">Demo</Badge>
          </div>
        )}

        {/*
          COPIA SEMPRE, VOTO QUANDO SI PUO' (Davide, 04/09/2026). Il voto ha
          bisogno dell'id del messaggio e sparisce sulle risposte di prova; la
          copia no, e una risposta si gira al collega anche quando non si vota.
        */}
        <div className="flex items-center gap-1">
          <BottoneCopia testo={msg.content} etichetta="Copia la risposta" />
          {showFeedback && (
            <FeedbackToolbar
              messageId={msg.messageId as string}
              conversationId={conversationId}
              domanda={domanda}
            />
          )}
        </div>
        {/* L'avvertenza "l'AI puo' sbagliare" sta sotto il composer, una
            volta sola per schermata: qui ripetuta rubava due righe a risposta. */}
      </div>
    </div>
  );
}

export function MessageList({ messages, loading, onPickSuggestion }: Props) {
  // Precarico in sottofondo: non blocca niente e toglie l'attesa alla prima
  // risposta.
  useEffect(() => {
    void import("./MarkdownContent");
  }, []);
  const endRef = useRef<HTMLDivElement>(null);
  const params = useParams();
  const conversationId = params.id ?? "";
  const [loadingStage, setLoadingStage] = useState<0 | 1 | 2>(0);
  const matcher = useAliasMatcher();

  const processText = (text: string): ReactNode => {
    if (!matcher.regex || !text) return text;
    const regex = new RegExp(matcher.regex.source, matcher.regex.flags);
    const out: ReactNode[] = [];
    let last = 0;
    let m: RegExpExecArray | null;
    let i = 0;
    while ((m = regex.exec(text)) !== null) {
      const entry = matcher.findByMatch(m[0]);
      if (m.index > last) out.push(text.slice(last, m.index));
      if (entry) {
        out.push(
          <AliasTooltip key={`${entry.sigla}-${m.index}-${i++}`} entry={entry}>
            {m[0]}
          </AliasTooltip>,
        );
      } else {
        out.push(m[0]);
      }
      last = m.index + m[0].length;
      if (m[0].length === 0) regex.lastIndex++;
    }
    if (last < text.length) out.push(text.slice(last));
    return out.length ? out : text;
  };

  useEffect(() => {
    if (!loading) {
      setLoadingStage(0);
      return;
    }
    const t1 = setTimeout(() => setLoadingStage(1), 2000);
    const t2 = setTimeout(() => setLoadingStage(2), 6000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [loading]);

  const loadingTexts = [
    "Ricerca nei manuali…",
    "Analizzo i documenti…",
    "Compongo la risposta…",
  ];

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, loading]);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-3 overflow-x-hidden px-3 py-4 pb-6 sm:px-5">
      {messages.map((m, i) =>
        m.role === "user" ? (
          <UserMessage key={m.id} text={m.content} imageUrl={m.imageUrl} />
        ) : (
          <AssistantMessage
            key={m.id}
            msg={m}
            onPickSuggestion={onPickSuggestion}
            conversationId={conversationId}
            processText={processText}
            // L'ultima domanda dell'utente prima di questa risposta: cosi' chi
            // scrive a Datacom non deve riscriverla a memoria.
            domanda={[...messages.slice(0, i)].reverse().find((x) => x.role === "user")?.content}
          />
        ),
      )}
      {loading && (
        <div className="flex items-center gap-3 px-1 text-sm text-muted-foreground animate-fade-up">
          <div className="dot-flashing" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <span>{loadingTexts[loadingStage]}</span>
        </div>
      )}
      <div ref={endRef} />
    </div>
  );
}